/**
 * Category-aware treatment form. Renders the category selector pills plus the
 * matching treatment form (external / service / vaccine / internal), narrowing the
 * provided defaults to the active category. Shared by the consultation
 * workspace, the standalone edit panel, and the quick-prescribe panel.
 *
 * The parent owns the `category` state (so it can read it elsewhere) and the
 * surrounding wrapper element; this component only renders the selector + form.
 */
import type { ClinicId, PrescribingContext } from '@snootsvet/shared'

import { RadioGroup, RadioGroupItem } from '@/frontend/components/ui/radio-group'

import { ExternalTreatmentForm } from './external-treatment.form'
import { InternalTreatmentForm } from './internal-treatment.form'
import { ServiceTreatmentForm } from './service-treatment.form'
import type {
  ExternalFormItem,
  FormTreatmentItem,
  InternalFormItem,
  TreatmentCategory,
} from './treatments.types'
import { VaccineTreatmentForm } from './vaccine-treatment.form'

/** Canonical category pills, in display order. Filter to restrict the choices. */
export const TREATMENT_CATEGORY_OPTIONS: { value: TreatmentCategory; label: string }[] = [
  { value: 'prescription', label: 'Medication / Consumable' },
  { value: 'vaccination', label: 'Vaccine' },
  { value: 'service', label: 'Service' },
  { value: 'external', label: 'External Script' },
]

/** Type guard for external prescription items. */
function isExternal(item: FormTreatmentItem): item is ExternalFormItem {
  return item.treatmentCategory === 'external'
}

/** Type guard for items backed by clinic stock (internal variants). */
function isInternal(item: FormTreatmentItem): item is InternalFormItem {
  return item.treatmentCategory !== 'external'
}

interface TreatmentFormProps {
  patientId: string
  category: TreatmentCategory
  /** Omit to render the selector read-only. It's also locked while `editing`. */
  onCategoryChange?: (category: TreatmentCategory) => void
  defaults?: FormTreatmentItem
  prescribingContext: PrescribingContext
  contextClinicId?: ClinicId
  /** Patient's species — narrows the vaccine form's disease options. Required, not optional:
   *  a caller that passes `category` directly reaches the vaccine form regardless of
   *  `prescribingContext`, so omitting it silently offered every species' diseases. */
  patientSpecies: string
  defaultCreateTask?: boolean
  onSave: (item: FormTreatmentItem) => void
  onCancel: () => void
  editing?: boolean
  saving?: boolean
  /** Appended to the form `key` to force a remount (e.g. loading a previous treatment). */
  remountKey?: string | number
}

export function TreatmentForm({
  patientId,
  category,
  onCategoryChange,
  defaults,
  prescribingContext,
  contextClinicId,
  patientSpecies,
  defaultCreateTask,
  onSave,
  onCancel,
  editing,
  saving,
  remountKey,
}: TreatmentFormProps) {
  // Services and vaccines are administered in consult, so they're only offered in a
  // clinic consultation. The selector is locked while `editing` (category can't change).
  const categoryOptions =
    prescribingContext === 'clinic_consultation'
      ? TREATMENT_CATEGORY_OPTIONS
      : TREATMENT_CATEGORY_OPTIONS.filter((o) => o.value !== 'service' && o.value !== 'vaccination')

  const internalDefault = defaults && isInternal(defaults) ? defaults : undefined
  const externalDefault = defaults && isExternal(defaults) ? defaults : undefined
  // A service default is always an in-consult administered item.
  const serviceDefault =
    internalDefault?.type === 'internal_administered' &&
    internalDefault.treatmentCategory === 'service'
      ? internalDefault
      : undefined
  // A vaccine default is always an in-consult administered item.
  const vaccineDefault =
    internalDefault?.type === 'internal_administered' &&
    internalDefault.treatmentCategory === 'vaccination'
      ? internalDefault
      : undefined

  const key = `${category}${remountKey != null ? `-${remountKey}` : ''}`

  return (
    <>
      <RadioGroup
        value={category}
        onValueChange={onCategoryChange}
        variant="pills"
        size="sm"
        disabled={editing}
      >
        {categoryOptions.map((opt) => (
          <RadioGroupItem key={opt.value} value={opt.value}>
            {opt.label}
          </RadioGroupItem>
        ))}
      </RadioGroup>

      {category === 'external' ? (
        <ExternalTreatmentForm
          key={key}
          defaultValues={externalDefault}
          defaultCreateTask={defaultCreateTask}
          prescribingContext={prescribingContext}
          onSave={onSave}
          onCancel={onCancel}
          editing={editing}
          saving={saving}
        />
      ) : category === 'service' ? (
        <ServiceTreatmentForm
          key={key}
          defaultValues={serviceDefault}
          contextClinicId={contextClinicId}
          onSave={onSave}
          onCancel={onCancel}
          editing={editing}
          saving={saving}
        />
      ) : category === 'vaccination' ? (
        <VaccineTreatmentForm
          key={key}
          defaultValues={vaccineDefault}
          contextClinicId={contextClinicId}
          patientSpecies={patientSpecies}
          onSave={onSave}
          onCancel={onCancel}
          editing={editing}
          saving={saving}
        />
      ) : (
        <InternalTreatmentForm
          key={key}
          patientId={patientId}
          defaultValues={internalDefault}
          prescribingContext={prescribingContext}
          contextClinicId={contextClinicId}
          onSave={onSave}
          onCancel={onCancel}
          editing={editing}
          saving={saving}
        />
      )}
    </>
  )
}
